// ===========================================
// COMPONENTE: ServiciosFiltros
// ===========================================
// Barra de filtros compacta para la tabla de servicios

import * as React from 'react'
import { Search, X, RefreshCw } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import {
  CATEGORIAS_SERVICIO_OPTIONS,
  ESTADOS_SERVICIO_OPTIONS,
  CATEGORIA_SERVICIO_LABELS,
  ESTADO_SERVICIO_LABELS,
  type CategoriaServicio,
  type EstadoServicio,
} from './forms'
import type { ServiciosFiltrosState } from './useServiciosFiltrosURL'

interface ServiciosFiltrosProps {
  filtros: ServiciosFiltrosState
  onChange: (filtros: ServiciosFiltrosState) => void
  onRefresh?: () => void
  isRefreshing?: boolean
  className?: string
}

export function ServiciosFiltros({
  filtros,
  onChange,
  onRefresh,
  isRefreshing,
  className,
}: ServiciosFiltrosProps) {
  // Contar filtros activos (excluyendo búsqueda)
  const filtrosActivos = React.useMemo(() => {
    let count = 0
    if (filtros.categoria && filtros.categoria !== 'todos') count++
    if (filtros.estado && filtros.estado !== 'todos') count++
    return count
  }, [filtros])

  // Handler para cambiar un campo
  const handleChange = (campo: keyof ServiciosFiltrosState, valor: string) => {
    onChange({ ...filtros, [campo]: valor })
  }

  // Handler para limpiar todos los filtros
  const handleLimpiarFiltros = () => {
    onChange({
      busqueda: '',
      categoria: 'todos',
      estado: 'todos',
    })
  }

  return (
    <div className={cn('space-y-2', className)}>
      {/* Fila principal: Búsqueda + Filtros + Refresh */}
      <div className="flex items-center gap-2 flex-wrap">
        {/* Búsqueda */}
        <div className="relative min-w-[180px] flex-1 max-w-sm">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            aria-label="Buscar servicios por nombre"
            placeholder="Buscar por nombre..."
            value={filtros.busqueda}
            onChange={(e) => handleChange('busqueda', e.target.value)}
            className="pl-8 h-9"
          />
        </div>

        {/* Categoría */}
        <Select
          value={filtros.categoria}
          onValueChange={(v) => handleChange('categoria', v)}
        >
          <SelectTrigger className="w-[180px] h-9">
            <span className="truncate">
              <span className="text-muted-foreground">Categoría:</span>{' '}
              {filtros.categoria === 'todos'
                ? 'Todas'
                : CATEGORIA_SERVICIO_LABELS[filtros.categoria as CategoriaServicio]}
            </span>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todas</SelectItem>
            {CATEGORIAS_SERVICIO_OPTIONS.map((cat) => (
              <SelectItem key={cat.value} value={cat.value}>
                {cat.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Estado */}
        <Select
          value={filtros.estado}
          onValueChange={(v) => handleChange('estado', v)}
        >
          <SelectTrigger className="w-[160px] h-9">
            <span className="truncate">
              <span className="text-muted-foreground">Estado:</span>{' '}
              {filtros.estado === 'todos'
                ? 'Todos'
                : ESTADO_SERVICIO_LABELS[filtros.estado as EstadoServicio]}
            </span>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos</SelectItem>
            {ESTADOS_SERVICIO_OPTIONS.map((est) => (
              <SelectItem key={est.value} value={est.value}>
                {est.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Limpiar filtros */}
        {filtrosActivos > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLimpiarFiltros}
            className="h-9 text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4 mr-1" />
            <span className="hidden sm:inline">Limpiar</span>
          </Button>
        )}

        {/* Refresh */}
        {onRefresh && (
          <Button
            variant="outline"
            size="icon"
            onClick={onRefresh}
            disabled={isRefreshing}
            className="h-9 w-9"
          >
            <RefreshCw className={cn('h-4 w-4', isRefreshing && 'animate-spin')} />
          </Button>
        )}
      </div>
    </div>
  )
}
